import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, StarIcon, TagIcon, SparklesIcon, UtensilsIcon, GiftIcon, TrendingUpIcon } from 'lucide-react';
interface Restaurant {
  id: string;
  name: string;
  description: string;
  cuisine: string;
  location: string;
  thumbnail: string;
  rating: number;
  priceRange: string;
  pricePerPax: number;
  availableSeats: number;
  totalSeats: number;
  specialPackages: string[];
  dietaryOptions: string[];
}
interface RestaurantCardProps {
  restaurant: Restaurant;
}
export function RestaurantCard({
  restaurant
}: RestaurantCardProps) {
  const hasRamadanPackage = restaurant.specialPackages.includes('Ramadan Iftar Special');
  const hasMultiplePackages = restaurant.specialPackages.length > 1;
  return <Link to={`/restaurant/${restaurant.id}`} className="h-full" data-id="element-122">
      <div className="group bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col border border-slate-200" data-id="element-123">
        <div className="relative h-56 overflow-hidden flex-shrink-0" data-id="element-124">
          <img src={restaurant.thumbnail} alt={restaurant.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-125" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" data-id="element-126"></div>
          {hasRamadanPackage && <div className="absolute top-4 right-4 bg-gradient-to-r from-rose-700 to-rose-800 text-white px-5 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 shadow-xl animate-pulse border border-rose-600" data-id="element-127">
              <SparklesIcon className="w-4 h-4" data-id="element-128" />
              Ramadan Special
            </div>}
          <div className="absolute bottom-4 left-4 right-4" data-id="element-129">
            <div className="flex items-center justify-between text-white" data-id="element-130">
              <div className="flex items-center gap-2" data-id="element-131">
                <MapPinIcon className="w-4 h-4" data-id="element-132" />
                <span className="text-sm font-semibold tracking-wide" data-id="element-133">
                  {restaurant.location}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/30" data-id="element-134">
                <UtensilsIcon className="w-4 h-4" data-id="element-135" />
                <span className="text-sm font-semibold" data-id="element-136">
                  {restaurant.cuisine}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 flex flex-col flex-grow" data-id="element-137">
          <div className="flex items-start justify-between mb-3" data-id="element-138">
            <h3 className="text-2xl font-bold text-slate-900 group-hover:text-rose-700 transition-colors flex-1 tracking-tight" data-id="element-139">
              {restaurant.name}
            </h3>
            <span className="text-lg font-bold text-slate-700 ml-2" data-id="element-140">
              {restaurant.priceRange}
            </span>
          </div>
          <p className="text-slate-600 text-sm mb-4 line-clamp-2 leading-relaxed" data-id="element-141">
            {restaurant.description}
          </p>
          <div className="flex items-center gap-3 mb-4 flex-wrap" data-id="element-142">
            <div className="flex items-center gap-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white px-3 py-1 rounded-full" data-id="element-143">
              <StarIcon className="w-4 h-4 fill-current" data-id="element-144" />
              <span className="font-bold text-sm" data-id="element-145">{restaurant.rating}</span>
            </div>
            {hasMultiplePackages && <div className="flex items-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full" data-id="element-146">
                <GiftIcon className="w-4 h-4" data-id="element-147" />
                <span className="text-sm font-medium" data-id="element-148">
                  {restaurant.specialPackages.length} Offers
                </span>
              </div>}
            {restaurant.pricePerPax < 400 && <div className="flex items-center gap-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full" data-id="element-149">
                <TrendingUpIcon className="w-4 h-4" data-id="element-150" />
                <span className="text-sm font-medium" data-id="element-151">Great Value</span>
              </div>}
          </div>
          {restaurant.dietaryOptions.length > 0 && <div className="flex flex-wrap gap-2 mb-4" data-id="element-152">
              {restaurant.dietaryOptions.slice(0, 3).map((option, idx) => <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full" data-id="element-153">
                  {option}
                </span>)}
            </div>}
          {restaurant.specialPackages.length > 0 && <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-3 mb-4" data-id="element-154">
              <div className="flex items-start gap-2" data-id="element-155">
                <TagIcon className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" data-id="element-156" />
                <div data-id="element-157">
                  <p className="text-xs text-purple-800 font-semibold mb-1" data-id="element-158">
                    Special Packages Available
                  </p>
                  <p className="text-xs text-purple-700" data-id="element-159">
                    {restaurant.specialPackages.slice(0, 2).join(' • ')}
                    {restaurant.specialPackages.length > 2 && ' • More'}
                  </p>
                </div>
              </div>
            </div>}
          <div className="flex items-center justify-between pt-4 border-t border-slate-200 mt-auto" data-id="element-160">
            <div data-id="element-161">
              <p className="text-xs text-slate-500 mb-1 font-medium" data-id="element-162">
                Starting from
              </p>
              <p className="text-2xl font-bold bg-gradient-to-r from-rose-700 to-rose-800 bg-clip-text text-transparent" data-id="element-163">
                MVR {restaurant.pricePerPax}
              </p>
              <p className="text-xs text-slate-500" data-id="element-164">per person</p>
            </div>
            <button className="bg-gradient-to-r from-rose-700 to-rose-800 hover:from-rose-800 hover:to-rose-900 text-white px-6 py-3 rounded-xl font-bold transition-all transform hover:scale-105 shadow-lg tracking-wide" data-id="element-165">
              Book Now
            </button>
          </div>
        </div>
      </div>
    </Link>;
}